import {
  type DocumentEvidenceCitation,
  evidenceDocumentIcon,
  useOptionalDocumentEvidenceInteraction,
} from "@/features/rich-text/evidence";
import { cn } from "@/lib/utils";
import {
  type AssistantDocumentCitationGroup,
  type AssistantDocumentEvidenceDocument,
  groupAssistantDocumentCitationsByDocument,
} from "../lib/ai-assistant-message";

const MARKER_CLASSES = "inline-flex max-w-full items-center gap-1 text-xs text-muted-foreground";

/**
 * One citation per passage the answer read from this document. Whole-block locators — the
 * embedded part data has no character span — which `documentEvidence.present` accepts, so
 * the popover resolves live names and excerpts rather than reusing the snapshot below.
 */
function buildDocumentCitations(
  entry: AssistantDocumentEvidenceDocument
): DocumentEvidenceCitation[] {
  return entry.blockIds.map((blockId) => ({
    reference: {
      kind: "document_block",
      artifactId: entry.artifactId,
      processingRevisionId: entry.processingRevisionId,
      blockId,
    },
    fallbackLabel: entry.documentName,
  }));
}

/**
 * The compact marker for one document behind an answer: its name, and how many passages were
 * read from it. Clicking opens the shared citation popover, which is the only place passages
 * become readable — and deliberately the only citation text there is, since a raw
 * `processingRevisionId` or `blockId` is transport detail, not a citation (LAT-838 / RC-15).
 */
function AssistantDocumentEvidenceMarker({ entry }: { entry: AssistantDocumentEvidenceDocument }) {
  const evidenceInteraction = useOptionalDocumentEvidenceInteraction();
  const count = entry.blockIds.length;
  // The footer holds a snapshot rather than a live presentation, so the glyph is read from
  // the document's name — which for an answer's citations is normally the file name itself.
  const Icon = evidenceDocumentIcon({ documentName: entry.documentName });
  const content = (
    <>
      <Icon className="size-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate">{entry.documentName}</span>
      {count > 1 ? <sup className="self-start text-[0.625rem] leading-none">{count}</sup> : null}
    </>
  );

  // Outside a provider (a surface that renders a transcript without the evidence runtime)
  // the marker is still honest about what backs the answer — it simply cannot be opened.
  if (!evidenceInteraction) {
    return <span className={MARKER_CLASSES}>{content}</span>;
  }

  return (
    <button
      type="button"
      className={cn(
        MARKER_CLASSES,
        "cursor-pointer transition-colors hover:text-foreground",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      )}
      aria-haspopup="dialog"
      // The provider re-finds this marker by key when the transcript re-renders it under an
      // open popover (LAT-980); its citations live only in React props.
      data-evidence-anchor-key={`assistant-citations:${entry.key}`}
      aria-label={
        count > 1
          ? `View document evidence: ${entry.documentName} (${count} passages)`
          : `View document evidence: ${entry.documentName}`
      }
      title={count > 1 ? `View document evidence (${count})` : "View document evidence"}
      onClick={(event) => {
        evidenceInteraction.activateEvidenceGroup(
          buildDocumentCitations(entry),
          event.currentTarget
        );
      }}
    >
      {content}
    </button>
  );
}

export function AssistantDocumentCitations({
  citations,
}: {
  citations: AssistantDocumentCitationGroup[];
}) {
  const documents = groupAssistantDocumentCitationsByDocument(citations);

  if (documents.length === 0) return null;

  return (
    <div className="mt-4 pt-3">
      <p className="text-xs font-medium text-muted-foreground">Document evidence</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {documents.map((entry) => (
          <AssistantDocumentEvidenceMarker key={entry.key} entry={entry} />
        ))}
      </div>
    </div>
  );
}
